import { DistributionType } from "~src/pages/brands/Detail/TrafficAndRevenue";

import { TrafficData } from "~src/types/brand";
import { AgeDistribution, GenderDistribution } from "./CompetitorAnalytics";

export type GraphData = {
  date: string[];
  data: number[];
};

export type GenderGraphDataType = { keys: string[]; values: number[] };

export type AgeGraphDataType = {
  keys: string[];
  values: number[];
};

export const generateGenderGraphData = (
  data: GenderDistribution[],
): GenderGraphDataType => {
  const totals: { [key: string]: number } = {};
  const counts: { [key: string]: number } = {};

  data.forEach((entry) => {
    Object.keys(entry).forEach((key) => {
      const value = entry[key as keyof GenderDistribution];
      if (!totals[key]) {
        totals[key] = 0;
        counts[key] = 0;
      }
      if (value !== "--") {
        totals[key] += parseFloat(value.replace("%", ""));
        counts[key] += 1;
      }
    });
  });

  const keys = Object.keys(totals);
  const values = keys.map((key) =>
    counts[key] > 0 ? totals[key] / counts[key] : 0,
  );

  return { keys, values };
};

export const generateAgeGraphData = (
  inputArray: AgeDistribution[],
): AgeGraphDataType[] => {
  return inputArray.map((obj) => {
    const keys = Object.keys(obj);
    const values = keys.map((key) => {
      const value = obj[key as keyof AgeDistribution].replace("%", "");
      return value === "--" ? 0 : parseFloat(value);
    });
    return { keys, values };
  });
};

export const generateAudienceDemographicsGraphData = (
  graphData: DistributionType | undefined,
) => {
  if (graphData?.AgeDistribution) {
    const keys = Object.keys(graphData.AgeDistribution);
    const values = Object.values(graphData.AgeDistribution).map((value) =>
      parseFloat(value.replace("%", "")),
    );
    const largeValueIndex = values.indexOf(Math.max(...values));
    const largestAgeGroup = keys[largeValueIndex];

    const isValidValues = values.every((value) => !isNaN(value));

    return { values, keys, largestAgeGroup, isValidValues };
  }
};

export const generateColors = (values: number[] | undefined) => {
  if (!values || values.length === 0) return [];
  const maxVal = Math.max(...values);
  const minVal = Math.min(...values);

  return values.map((value) => {
    const intensity = ((value - minVal) / (maxVal - minVal)) * 255;
    return `rgba(61, 115, 255, ${0.3 + (intensity / 255) * 0.7})`;
  });
};

export const generateRevenueGraphData = (
  dataArray?: TrafficData[],
): GraphData[] => {
  const graphDataArray: GraphData[] = [];

  if (dataArray && Array.isArray(dataArray)) {
    dataArray.forEach((data) => {
      const graphData: GraphData = { date: [], data: [] };
      if (data.revenue_overview_data) {
        graphData.date = Object.keys(data.revenue_overview_data);
        graphData.data = Object.values(data.revenue_overview_data);
      }
      graphDataArray.push(graphData);
    });
  }

  return graphDataArray;
};

export const parsePercentageValue = (traffic: string) => {
  const value = traffic?.replace("%", "");
  const precisedValue = parseFloat(value).toPrecision(2);
  // converting to int format as geomap doesn't accepts float values.
  return parseInt(precisedValue);
};

export const sortCountryAds = (data: any[]) => {
  // Step 1: Aggregate ad_count for each country
  const countryMap = new Map();

  data.forEach((item) => {
    const country = item.ad_country;
    if (countryMap.has(country)) {
      countryMap.get(country).ad_count += item.ad_count;
    } else {
      countryMap.set(country, { ...item });
    }
  });

  const aggregatedData = Array.from(countryMap.values());

  // Step 2: Sort the aggregated list by ad_count
  aggregatedData.sort((a, b) => b.ad_count - a.ad_count);

  // Step 3: Reposition "Others" country to the end of the list
  const targetCountry = "Others";
  const targetIndex = aggregatedData.findIndex(
    (item) => item.ad_country === targetCountry,
  );
  if (targetIndex !== -1) {
    const targetItem = aggregatedData.splice(targetIndex, 1)[0];
    aggregatedData.push(targetItem);
  }

  return aggregatedData;
};
